import {
  type WorkspacePostingAggregationResource,
  type WorkspacePostingJobTypeResource,
  type WorkspacePostingOpportunityTypeResource,
  type WorkspacePostingStatusResource,
} from '@/features/admin/api/resources/workspace-posting'
import { type WorkspaceApplicantStatusResource } from '@/features/admin/api/resources/workspace-applicant'
import {
  type WorkspacePostingJobType,
  type WorkspacePostingOpportunityType,
  type WorkspacePostingStatus,
} from './WorkspacePosting'
import { type WorkspaceApplicantStatus } from './WorkspaceApplicant'
import { type WorkspacePostingAggregation } from './WorkspacePostingAggregation'
import { match } from '@/utils/pattern-match-util'

export function mapWorkspacePostingAggregationResourceToAggregation(
  resource: WorkspacePostingAggregationResource,
): WorkspacePostingAggregation {
  return {
    status: mapWorkspacePostingStatusResourceToStatus(resource.status),
    total: parseInt(resource.total),
  }
}

export function mapWorkspacePostingStatusResourceToStatus(
  status: WorkspacePostingStatusResource,
): WorkspacePostingStatus {
  return match<WorkspacePostingStatusResource, WorkspacePostingStatus>(status, {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled',
    IN_REVIEW: 'InReview',
  })
}

export function mapWorkspacePostingStatusToResource(
  status: WorkspacePostingStatus,
): WorkspacePostingStatusResource | undefined {
  switch (status) {
    case 'Active':
      return 'ACTIVE'
    case 'Cancelled':
      return 'CANCELLED'
    case 'Closed':
      return 'CLOSED'
    case 'Draft':
      return 'DRAFT'
    case 'InReview':
      return 'IN_REVIEW'
    case 'Null':
      return undefined
  }
}

export function mapWorkspacePostingJobTypeResourceToJobType(
  jobType: WorkspacePostingJobTypeResource,
): WorkspacePostingJobType {
  // iam-api stores posting job types unvalidated, so an unexpected value must
  // not break the staffing manager.
  switch (jobType) {
    case 'PREP':
      return 'Prep'
    case 'REVIEW':
      return 'Review'
    case 'BOOKKEEPING':
      return 'Bookkeeping'
    default:
      console.warn('unknown workspace posting job type', jobType)
      return 'Null'
  }
}

export function mapWorkspacePostingJobTypeToResource(
  jobType: WorkspacePostingJobType,
): WorkspacePostingJobTypeResource | undefined {
  switch (jobType) {
    case 'Prep':
      return 'PREP'
    case 'Review':
      return 'REVIEW'
    case 'Bookkeeping':
      return 'BOOKKEEPING'
    case 'Null':
      return undefined
  }
}

export function mapWorkspacePostingOpportunityTypeResourceToOpportunityType(
  opportunityType: WorkspacePostingOpportunityTypeResource,
): WorkspacePostingOpportunityType {
  return match<
    WorkspacePostingOpportunityTypeResource,
    WorkspacePostingOpportunityType
  >(opportunityType, {
    OUTSOURCING: 'Outsourcing',
    SIGN_AND_FILE: 'SignAndFile',
    UNKNOWN: 'Unknown',
  })
}

export function mapWorkspacePostingOpportunityTypeToResource(
  opportunityType: WorkspacePostingOpportunityType,
): WorkspacePostingOpportunityTypeResource {
  switch (opportunityType) {
    case 'Outsourcing':
      return 'OUTSOURCING'
    case 'SignAndFile':
      return 'SIGN_AND_FILE'
    case 'Unknown':
      return 'UNKNOWN'
  }
}

export function mapWorkspaceApplicantStatusResourceToStatus(
  status: WorkspaceApplicantStatusResource,
): WorkspaceApplicantStatus {
  return match<WorkspaceApplicantStatusResource, WorkspaceApplicantStatus>(
    status,
    {
      APPLIED: 'Applied',
      REJECTED: 'Rejected',
      INVITED: 'Invited',
      ACCEPTED: 'Accepted',
      WITHDRAWN: 'Withdrawn',
      EXPIRED: 'Expired',
    },
  )
}
